import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type { InvoiceEntity } from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { dealQueryKey } from './useDealQuery'
import { invoicesQueryKey } from './useInvoicesQuery'

export interface RestoreInvoiceVariables {
  dealId: string
  invoiceId: string
}

interface Snapshot {
  invoicesSnapshot: { key: readonly unknown[]; data: InvoiceEntity[] | undefined }
}

function patchInvoiceStatus(
  invoices: InvoiceEntity[],
  invoiceId: string,
  status: InvoiceEntity['status'],
): InvoiceEntity[] {
  return invoices.map((invoice) =>
    invoice.id === invoiceId ? { ...invoice, status } : invoice,
  )
}

export function useRestoreInvoiceMutation(): UseMutationResult<
  InvoiceEntity,
  unknown,
  RestoreInvoiceVariables,
  Snapshot
> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<InvoiceEntity, unknown, RestoreInvoiceVariables, Snapshot>(
    ({ dealId, invoiceId }) => dealsAPI.restoreInvoice(dealId, invoiceId),
    {
      onMutate: async ({ dealId, invoiceId }): Promise<Snapshot> => {
        await queryClient.cancelQueries({ queryKey: invoicesQueryKey(dealId) })

        const invoicesKey = invoicesQueryKey(dealId)
        const invoicesData = queryClient.getQueryData<InvoiceEntity[]>(invoicesKey)
        if (invoicesData) {
          queryClient.setQueryData<InvoiceEntity[]>(
            invoicesKey,
            patchInvoiceStatus(invoicesData, invoiceId, 'Draft'),
          )
        }
        return { invoicesSnapshot: { key: invoicesKey, data: invoicesData } }
      },
      onError: (error, _vars, context) => {
        if (context) {
          queryClient.setQueryData(context.invoicesSnapshot.key, context.invoicesSnapshot.data)
        }
        handleError(error)
      },
      onSuccess: (invoice) => {
        toast.current?.show({
          severity: 'info',
          summary: 'Invoice restored',
          detail: `${invoice.invoiceNumber} is back in the active list.`,
          life: 3000,
        })
      },
      onSettled: (_data, _err, { dealId }) => {
        queryClient.invalidateQueries({ queryKey: invoicesQueryKey(dealId) })
        queryClient.invalidateQueries({ queryKey: dealQueryKey(dealId) })
      },
    },
  )
}
